import React, { useEffect, useMemo, useState } from 'react'
import Cluster from './Cluster'
import { Divider, Empty, Flex, Spin } from 'antd'
import { actions } from '../../../../store/reducers/assignment.reducer'
import { connect, useDispatch, useSelector } from 'react-redux'
import { DEFAULT_CLUSTER_SORTING_TYPE } from '../../../../constants/sorting-types'
import { CLUSTER_X_ID, CLUSTER_Y_ID } from '../../../../constants/cluster-ids'
import useQueryParam from '../../../../hooks/useQueryParam'
import {
    SORT_PARAM,
    STUDENT_FEEDBACK_EXACT_MATCH_PARAM,
    STUDENT_FEEDBACK_PARAM,
    SUBMISSION_SEARCH_PARAM,
} from '../../../../constants/query-params'
import { openNotificationWithIcon } from '../../../../utils/notification.helper'
import _, { isString } from 'lodash'
import {
    useFetchNotConfidentSubmissionsQuery,
    useFetchSubmissionReviewsQuery,
    useFilterByCriteriaQuery,
} from '../../../../store/apis/submission.api'
import { useParams } from 'react-router'
import { REVIEW_FILTERS } from '../../sections/clusters/filters/ReviewFilters'
import { sortClusters, sortClustersFiles } from '../../../../utils/cluster.helper'
import patternSelectors from '../../../../store/selectors/pattern.selectors'
import { submissionActions } from '../../../../store/slices/submission.slice'
import { PATTERN_TYPES } from '../../../../constants/pattern-types'

const ClusterGroup = ({
    assignment,
    clusters: rawClusters,
    filters,
    isWaitingForClusters,
    isWaitingForAssignmentInfo,
    isWaitingForFiles,
    isWaitingForPatterns,
    isWaitingForSelectSubgroup,
    setFilteredClusters,
    unitTestResults,
}) => {
    const finetunedPatternExists = useSelector(patternSelectors.finetunedPatternExists)
    const dispatch = useDispatch()

    const { token, layerId } = useParams()
    const searchParam = useQueryParam(SUBMISSION_SEARCH_PARAM)
    const sortParam = useQueryParam(SORT_PARAM) || DEFAULT_CLUSTER_SORTING_TYPE

    const { data: submissionReviews } = useFetchSubmissionReviewsQuery({ token, layerId })
    const { data: notConfidentSubmissions } = useFetchNotConfidentSubmissionsQuery(
        { token, layerId },
        { skip: !finetunedPatternExists },
    )

    const [criteriaFilteredFileIds, setCriteriaFilteredFileIds] = useState([])
    
    // Determine if we have criteria to filter
    const hasCriteriaToFilter = filters.patterns.some(pattern => 
        pattern.type === PATTERN_TYPES.SEMANTIC.value && 
        pattern.criteria && 
        pattern.criteria.some(criterion => criterion.enabled)
    )
    
    // Build criteria conditions for the query
    const criteriaConditions = useMemo(() => {
        if (!hasCriteriaToFilter) return []
        
        const conditions = []
        filters.patterns.forEach(pattern => {
            if (pattern.type === PATTERN_TYPES.SEMANTIC.value) {
                pattern.criteria.forEach((criterion, index) => {
                    if (criterion.enabled) {
                        conditions.push(`${pattern.id},${index},${criterion.show}`)
                    }
                })
            }
        })
        
        return conditions
    }, [filters.patterns, hasCriteriaToFilter])
    
    // Use the RTK Query hook with skip option when no criteria are enabled
    const { data: filteredFileIds, isError } = useFilterByCriteriaQuery(
        { token, layerId, criteriaConditions },
        { skip: criteriaConditions.length === 0 }
    )
    
    // Update the local state when the query results change
    useEffect(() => {
        if (isError) {
            console.error('Error applying criteria filtering')
            setCriteriaFilteredFileIds([])
        } else if (filteredFileIds) {
            setCriteriaFilteredFileIds(filteredFileIds)
        } else if (!hasCriteriaToFilter) {
            setCriteriaFilteredFileIds([])
        }
    }, [filteredFileIds, isError, hasCriteriaToFilter])

    // ---------------------------
    // 1) Student feedback filters from URL
    // ---------------------------
    const studentFeedbackParam = useQueryParam(STUDENT_FEEDBACK_PARAM) // e.g. "liked,disliked,commented"
    const studentFeedbackExactMatchParam = useQueryParam(STUDENT_FEEDBACK_EXACT_MATCH_PARAM) // "true" / "false"
    const studentFeedbackCheckedValues = studentFeedbackParam ? studentFeedbackParam.split(',') : []
    const studentFeedbackExactMatch = studentFeedbackExactMatchParam === 'true'

    const clusterBlocks = useMemo(() => {
        let _clusters = JSON.parse(JSON.stringify(rawClusters))
        
        const hasCriteriaToFilter = filters.patterns.some(pattern => 
            pattern.type === PATTERN_TYPES.SEMANTIC.value && 
            pattern.criteria && 
            pattern.criteria.some(criterion => criterion.enabled)
        )

        const shouldApplyFiltering = () =>
            searchParam ||
            filters.patterns.some(pattern => pattern.enabled) ||
            filters.feedback.missingPatternFeedback ||
            filters.feedback.missingGroupFeedback ||
            Object.values(filters.properties).some(property => property) ||
            filters.unitTests.some(unitTest => unitTest.enabled) ||
            filters.review !== REVIEW_FILTERS.ALL ||
            // Student feedback filtering
            studentFeedbackCheckedValues.length > 0 ||
            // Criteria filtering
            hasCriteriaToFilter

        if (shouldApplyFiltering()) {
            const fileIdsToSelect = []
            _clusters = _clusters.filter(cluster => {
                // -------------------------------------
                // Filter files inside the cluster
                // -------------------------------------
                const updatedClusterFiles = cluster?.files?.filter(file => {
                    // If criteria filtering is active and this file is not in the criteria-filtered IDs, exclude it
                    if (hasCriteriaToFilter && criteriaFilteredFileIds.length > 0 && !criteriaFilteredFileIds.includes(file.id)) {
                        return false
                    }
                    
                    // ---------------------------
                    // A) Student feedback check
                    // ---------------------------
                    // Assume file.feedback is something like:
                    // { thumbsUp: true, thumbsDown: false, comment: "text..." }
                    // Mapping to "liked", "disliked", "commented"
                    const fileStudentFeedbackFlags = {
                        liked: file.student_feedback?.liked,
                        disliked: file.student_feedback ? !file.student_feedback.liked : false,
                        commented: Boolean(file.student_feedback?.comment?.trim()),
                    }

                    let passesStudentFeedbackFilter = true
                    if (studentFeedbackCheckedValues.length > 0) {
                        if (studentFeedbackExactMatch) {
                            // Must match ALL selected feedback states
                            passesStudentFeedbackFilter = studentFeedbackCheckedValues.every(
                                v => fileStudentFeedbackFlags[v] === true,
                            )
                        } else {
                            // Must match at least one
                            passesStudentFeedbackFilter = studentFeedbackCheckedValues.some(
                                v => fileStudentFeedbackFlags[v] === true,
                            )
                        }
                    }
                    if (!passesStudentFeedbackFilter) return false

                    // ---------------------------
                    // B) Patterns (enabled vs ignored)
                    // ---------------------------
                    const enabledPatternFilterIds = filters.patterns.reduce((result, pattern) => {
                        if (pattern.enabled && pattern.show) result.push(pattern.id)
                        return result
                    }, [])
                    const checkIfFileHasPattern = id =>
                        file.motifList.some(motif => Number(motif.motif_id) === Number(id))
                    const fileMatchesPatternsThatShouldBeShown = filters.exactMatch
                        ? enabledPatternFilterIds.every(checkIfFileHasPattern)
                        : enabledPatternFilterIds.length
                          ? enabledPatternFilterIds.some(checkIfFileHasPattern)
                          : true

                    const patternFilterIdsThatShouldBeIgnored = filters.patterns.reduce((result, pattern) => {
                        if (pattern.enabled && !pattern.show) result.push(pattern.id)
                        return result
                    }, [])
                    const fileMatchesPatternsThatShouldBeIgnored = filters.exactMatch
                        ? patternFilterIdsThatShouldBeIgnored.some(checkIfFileHasPattern)
                        : patternFilterIdsThatShouldBeIgnored.every(checkIfFileHasPattern)

                    let fileMatchesAllPatternFilters = true
                    if (enabledPatternFilterIds.length && patternFilterIdsThatShouldBeIgnored.length) {
                        fileMatchesAllPatternFilters = filters.exactMatch
                            ? fileMatchesPatternsThatShouldBeShown && !fileMatchesPatternsThatShouldBeIgnored
                            : fileMatchesPatternsThatShouldBeShown || !fileMatchesPatternsThatShouldBeIgnored
                    } else if (enabledPatternFilterIds.length) {
                        fileMatchesAllPatternFilters = fileMatchesPatternsThatShouldBeShown
                    } else if (patternFilterIdsThatShouldBeIgnored.length) {
                        fileMatchesAllPatternFilters = !fileMatchesPatternsThatShouldBeIgnored
                    }

                    // ---------------------------
                    // C) Missing pattern feedback
                    // ---------------------------
                    const somePatternInFileIsMissingFeedback = filters.feedback.missingPatternFeedback
                        ? file.motifList.some(motif => {
                              const motifQuery = assignment.patterns.find(motifQuery => {
                                  return Number(motif.motif_id) === Number(motifQuery.motif_id)
                              })
                              return !motifQuery.feedback
                          })
                        : true

                    // ---------------------------
                    // D) Unit tests
                    // ---------------------------
                    const fileMatchAllUnitTestFilters = filters.unitTests.every(unitTest => {
                        if (!unitTest.enabled) {
                            return true
                        }
                        try {
                            return (
                                unitTest.passed ===
                                Boolean(
                                    unitTestResults.find(
                                        unitTestResult =>
                                            unitTestResult.file_id === file.id && unitTestResult.ut_id === unitTest.id,
                                    )?.ut_passed,
                                )
                            )
                        } catch (e) {
                            console.log('Results for that file were not found: ' + e)
                            return false
                        }
                    })

                    // ---------------------------
                    // E) Search param
                    // ---------------------------
                    let fileMatchSearchValue = true
                    if (searchParam?.startsWith('/') && searchParam?.endsWith('/')) {
                        try {
                            if (new RegExp(searchParam.slice(1, -1)).test(assignment.files[file.id]?.content)) {
                                fileIdsToSelect.push(file.id)
                            }
                        } catch {
                            openNotificationWithIcon('warning', 'The regex is invalid')
                        }
                    } else {
                        fileMatchSearchValue =
                            assignment.files && searchParam
                                ? file.name?.toLowerCase().includes(searchParam.toLowerCase()) ||
                                  (isString(assignment.files[file.id]?.content) &&
                                      assignment.files[file.id].content
                                          .toLowerCase()
                                          .includes(searchParam.toLowerCase()))
                                : true
                    }

                    // ---------------------------
                    // F) isFlaky
                    // ---------------------------
                    const fileMatchesIsFlaky = filters.properties.isFlaky
                        ? file.motifList.some(motif =>
                              assignment.patterns.some(
                                  pattern => motif.motif_id === pattern.motif_id && pattern.is_flaky,
                              ),
                          )
                        : true

                    // ---------------------------
                    // G) Reviewed / Unreviewed
                    // ---------------------------
                    let fileMatchesReview = true
                    if (filters.review === REVIEW_FILTERS.UNREVIEWED) {
                        fileMatchesReview = !submissionReviews?.some(review => review.submissionId === file.id)
                    } else if (filters.review === REVIEW_FILTERS.REVIEWED) {
                        fileMatchesReview = submissionReviews?.some(review => review.submissionId === file.id)
                    }

                    return (
                        fileMatchesAllPatternFilters &&
                        somePatternInFileIsMissingFeedback &&
                        fileMatchAllUnitTestFilters &&
                        fileMatchSearchValue &&
                        fileMatchesIsFlaky &&
                        fileMatchesReview
                    )
                })

                if (updatedClusterFiles) {
                    cluster.files = updatedClusterFiles
                }
                return filters.feedback.missingGroupFeedback ? !cluster.feedback : true
            })

            dispatch(submissionActions.setSelectedSubmissionIds(fileIdsToSelect))
        }

        _clusters = _clusters.filter(cluster => cluster !== undefined && cluster !== null)

        _clusters = sortClusters(_clusters, sortParam)
        _clusters = sortClustersFiles(_clusters, notConfidentSubmissions)

        const indexOfClusterX = _clusters.findIndex(cluster => cluster?.cluster_id === CLUSTER_X_ID)
        const clusterX = indexOfClusterX !== -1 ? _clusters.splice(indexOfClusterX, 1)[0] : null

        const indexOfClusterY = _clusters.findIndex(cluster => cluster?.cluster_id === CLUSTER_Y_ID)
        const clusterY = indexOfClusterY !== -1 ? _clusters.splice(indexOfClusterY, 1)[0] : null

        setFilteredClusters([..._clusters, ...(clusterX ? [clusterX] : []), ...(clusterY ? [clusterY] : [])])

        const clusterComponents = [..._clusters, clusterX, clusterY]
            .filter(Boolean)
            .map(cluster => <Cluster key={cluster.cluster_id} cluster={_.cloneDeep(cluster)} />)

        if (clusterComponents.length > 2 && clusterX && clusterY) {
            clusterComponents.splice(clusterComponents.length - 2, 0, <Divider key='divider' />)
        }

        return clusterComponents
    }, [
        assignment,
        rawClusters,
        filters,
        unitTestResults,
        sortParam,
        searchParam,
        submissionReviews,
        notConfidentSubmissions,
        studentFeedbackCheckedValues,
        studentFeedbackExactMatch,
        token,
        layerId,
        dispatch,
        setFilteredClusters,
        criteriaFilteredFileIds,
    ])

    let loadingMessage
    if (isWaitingForAssignmentInfo) {
        loadingMessage = 'Loading assignment...'
    } else if (isWaitingForClusters) {
        loadingMessage = 'Loading groups...'
    } else if (isWaitingForPatterns) {
        loadingMessage = 'Loading patterns...'
    } else if (isWaitingForFiles) {
        loadingMessage = 'Loading files...'
    } else if (isWaitingForSelectSubgroup) {
        loadingMessage = 'Looking for subgroups...'
    }

    let clusters

    if (clusterBlocks.length) {
        clusters = clusterBlocks
    } else {
        clusters = (
            <div className='d-flex-center' style={{ height: '50vh' }}>
                {!loadingMessage ? <Empty description='There are no groups' /> : null}
            </div>
        )
    }

    return (
        <Spin wrapperClassName='clusters__container' spinning={Boolean(loadingMessage)} tip={loadingMessage}>
            <Flex gap={16} vertical>
                {clusters}
            </Flex>
        </Spin>
    )
}

const mapState = ({ assignment }) => {
    return {
        assignment: assignment.assignment,
        clusters: assignment.clusters,
        filters: assignment.filters,
        isWaitingForClusters: assignment.isWaitingForClusters,
        isWaitingForAssignmentInfo: assignment.isWaitingForAssignmentInfo,
        isWaitingForFiles: assignment.isWaitingForFiles,
        isWaitingForPatterns: assignment.isWaitingForPatterns,
        isWaitingForSelectSubgroup: assignment.isWaitingForSelectSubgroup,
        unitTestResults: assignment.unitTestResults,
    }
}

const mapDispatch = dispatch => {
    return {
        setFilteredClusters(value) {
            dispatch(actions.setFilteredClusters(value))
        }
    }
}

export default connect(mapState, mapDispatch)(ClusterGroup)
